import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge"
import { AxiosError } from "axios";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Estrae un messaggio di errore leggibile da una risposta API.
 * Gestisce correttamente gli errori di validazione Pydantic che restituiscono
 * `detail` come array di oggetti invece di una stringa.
 * 
 * @param error - L'errore catturato (tipicamente AxiosError)
 * @param fallback - Messaggio di fallback se non è possibile estrarre un messaggio
 * @returns Una stringa leggibile per l'utente
 */
export function getErrorMessage(error: unknown, fallback: string = 'Si è verificato un errore'): string {
  if (!error) return fallback;

  // Se è un AxiosError con response
  const axiosError = error as AxiosError<{ detail?: unknown }>;
  const detail = axiosError.response?.data?.detail;

  if (detail === undefined || detail === null) {
    // Prova a usare il messaggio dell'errore standard
    if (error instanceof Error) {
      return error.message || fallback;
    }
    return fallback;
  }

  // Se detail è già una stringa, restituiscila
  if (typeof detail === 'string') {
    return detail;
  }

  // Se detail è un array (errori di validazione Pydantic)
  if (Array.isArray(detail)) {
    // Formato Pydantic: [{ loc: ['body', 'field'], msg: 'error message', type: 'error_type' }]
    const messages = detail.map((err: any) => {
      if (typeof err === 'string') return err;
      if (err.msg) {
        const field = err.loc?.slice(-1)?.[0] || '';
        return field ? `${field}: ${err.msg}` : err.msg;
      }
      return JSON.stringify(err);
    });
    return messages.join(', ');
  }

  // Se detail è un oggetto con proprietà message
  if (typeof detail === 'object') {
    const detailObj = detail as Record<string, unknown>;
    if (detailObj.message && typeof detailObj.message === 'string') {
      return detailObj.message;
    }
    // Prova a serializzare l'oggetto in modo leggibile
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }

  return fallback;
}

/**
 * Formatta un numero come valuta in formato italiano.
 * Usa il punto come separatore delle migliaia e la virgola per i decimali.
 * 
 * @param amount - Il numero da formattare
 * @param decimals - Numero di decimali (default: 2)
 * @param showSymbol - Se mostrare il simbolo € (default: true)
 * @returns Stringa formattata, es: "€ 12.033,00"
 * 
 * @example
 * formatCurrency(12033) // "€ 12.033,00"
 * formatCurrency(1234.5) // "€ 1.234,50"
 * formatCurrency(99.9, 2, false) // "99,90"
 */
export function formatCurrency(
  amount: number | undefined | null, 
  decimals: number = 2, 
  showSymbol: boolean = true
): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return showSymbol ? '€ 0,00' : '0,00';
  }
  
  // Formatta con locale italiano
  const formatted = amount.toLocaleString('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return showSymbol ? `€ ${formatted}` : formatted;
}

/**
 * Formatta un prezzo unitario con l'unità di misura.
 * 
 * @param price - Il prezzo da formattare
 * @param unit - L'unità di misura (es: "kg", "pz", "lt")
 * @returns Stringa formattata, es: "€ 12,50/kg"
 */
export function formatUnitPrice(price: number | undefined | null, unit: string = 'pz'): string {
  return `${formatCurrency(price)}/${unit}`;
}

/**
 * Formatta un numero in formato italiano con separatore delle migliaia.
 * Usa il punto come separatore delle migliaia e la virgola per i decimali.
 * 
 * @param value - Il numero da formattare
 * @param decimals - Numero di decimali (default: 0 per numeri interi)
 * @returns Stringa formattata, es: "12.033" o "1.234,5"
 * 
 * @example
 * formatNumber(12033) // "12.033"
 * formatNumber(1234.567, 2) // "1.234,57"
 * formatNumber(42) // "42"
 */
export function formatNumber(
  value: number | undefined | null, 
  decimals: number = 0
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0';
  }
  
  return value.toLocaleString('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

